import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { volunteersApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { ApiErrorCode, getApiErrorCode } from "@/common/helper/apiErrorHelper";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { VolunteerFormType } from "@/pages/system-settings/volunteers/components/VolunteerForm.types";
import { volunteerValidationSchema } from "@/pages/system-settings/volunteers/components/VolunteerForm.utils";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useVolunteerCreation(onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const dropdownOptions = useDropdownOptions();

  const createVolunteerMutation = useMutation({
    mutationFn: (values: VolunteerFormType) =>
      volunteersApi.createVolunteer(
        {
          createVolunteerRequest: {
            username: values.username,
            salutation: values.salutation,
            title: values.title,
            lastName: values.lastName,
            firstName: values.firstName,
            street: values.street,
            houseNumber: values.houseNumber,
            zipCode: values.zipCode,
            city: values.city,
            country: values.country,
            email: values.email,
            phone: values.phone,
            remarks: values.remarks,
          },
        },
        // onError toasts every failure itself; without this the global middleware would toast too.
        withLocalErrorHandling
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Volunteers] });
      showSuccess(t("volunteers.create.successfullyCreated"));
      onClose();
    },
    // Without this the backend's username-uniqueness rejection would be swallowed and the dialog
    // would just sit there: no toast, no row, no clue. The dialog stays open so the user can fix it.
    onError: async (error) => {
      showError(
        (await getApiErrorCode(error)) === ApiErrorCode.VolunteerUsernameAlreadyTaken
          ? t("volunteers.usernameAlreadyTaken")
          : t("volunteers.create.saveFailed")
      );
    },
  });

  const validationSchema = useMemo(() => volunteerValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<VolunteerFormType>({
    initialValues: {
      username: "",
      salutation: null,
      title: null,
      lastName: "",
      firstName: "",
      street: "",
      houseNumber: "",
      zipCode: "",
      city: "",
      country: "",
      email: "",
      phone: null,
      remarks: null,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await createVolunteerMutation.mutateAsync(values);
    },
  });

  return { formik, dropdownOptions };
}
